import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isAxiosError } from 'axios';
import { TOKEN_EVENT } from '../utils/session';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Button, useToast as useChakraToast } from '@chakra-ui/react';
import { GlobalContext } from 'providers/Global';
import {
  pollNotifications,
  dismissNotification as dismissNotificationAPI,
  dismissAll as dismissAllAPI,
  markSeen as markSeenAPI,
} from '../api/notificationAPI';
import { connectNotificationStream } from '../api/notificationSSE';
import { ApplicationRoutes } from '../utils/routes';
import { Notification, NotificationContextType, SseNotificationEvent } from '../types/notification';

const FAILURE_THRESHOLD = 3;

const defaultContext: NotificationContextType = {
  notifications: [],
  unreadCount: 0,
  isLoading: true,
  isPanelOpen: false,
  warningVisible: false,
  lastCursor: null,
  lastCursorId: null,
  togglePanel: () => {},
  dismissNotification: () => {},
  dismissAll: () => {},
  markSeen: () => {},
  navigateToNotification: () => {},
};

export const NotificationContext = createContext<NotificationContextType>(defaultContext);

interface PendingUndo {
  notification: Notification;
  timer: ReturnType<typeof setTimeout>;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    connection: { accountId },
    triggerRefresh,
  } = useContext(GlobalContext);
  const navigate = useNavigate();
  const chakraToast = useChakraToast();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);
  const [lastCursor, setLastCursor] = useState<string | null>(null);
  const [lastCursorId, setLastCursorId] = useState<string | null>(null);

  const triggerRefreshRef = useRef(triggerRefresh);
  triggerRefreshRef.current = triggerRefresh;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pendingUndos = useRef<Map<string, PendingUndo>>(new Map());
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const failureCountRef = useRef(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  const fetchInitial = useCallback(async () => {
    if (!accountId) return;

    try {
      const response = await pollNotifications();
      const data = response?.data;
      if (!data) return;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount ?? 0);
      setLastCursor(data.nextCursor ?? null);
      setLastCursorId(data.nextCursorId ?? null);
      setIsLoading(false);
      failureCountRef.current = 0;
      setWarningVisible(false);
    } catch (err: unknown) {
      // A refused session: the session owner (hooks/useSession) signs in again,
      // and the new token's announcement fetches these again
      if (isAxiosError(err) && err.response?.status === 401) return;
      setIsLoading(false);
    }
  }, [accountId]);

  const handleSseMessage = useCallback((event: SseNotificationEvent) => {
    if (event.type === 'notification.new') {
      const incoming = event.notification;
      setNotifications(prev => {
        if (prev.some(n => n._id === incoming._id)) return prev;
        return [incoming, ...prev];
      });
      if (incoming.status === 'UNREAD') {
        setUnreadCount(prev => prev + 1);
      }
      if (incoming.type === 'TRANSACTION_COMPLETED') {
        triggerRefreshRef.current();
      }
    } else if (event.type === 'notification.dismissed') {
      setNotifications(prev => {
        const filtered = prev.filter(
          n => !(n.type === 'SIGNATURE_NEEDED' && n.transactionId === event.transactionId),
        );
        setUnreadCount(filtered.filter(n => n.status === 'UNREAD').length);
        return filtered;
      });
    }

    failureCountRef.current = 0;
    setWarningVisible(false);
  }, []);

  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !accountId) return;

    eventSourceRef.current = connectNotificationStream(
      token,
      handleSseMessage,
      () => {
        failureCountRef.current++;
        if (failureCountRef.current >= FAILURE_THRESHOLD) {
          setWarningVisible(true);
        }

        // On reconnect (EventSource auto-reconnects), re-fetch to catch missed notifications.
        // EventSource fires 'error' then reconnects — when it reconnects and gets data,
        // handleSseMessage resets failureCount. We re-fetch after a short delay to hydrate.
        setTimeout(() => {
          if (eventSourceRef.current?.readyState === EventSource.CONNECTING) {
            fetchInitial();
          }
        }, 2000);
      },
    );
  }, [accountId, handleSseMessage, fetchInitial]);

  // Listen for the 'token' event the session owner (hooks/useSession) sends when it
  // stores a different token. This avoids the race condition where connectSSE()
  // reads a stale/null token because signing in (async) hasn't finished yet when
  // accountId changes.
  const handleTokenEvent = useCallback(() => {
    // Close any existing SSE connection before opening a new one
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    fetchInitial();
    connectSSE();
  }, [fetchInitial, connectSSE]);

  useEffect(() => {
    window.addEventListener(TOKEN_EVENT, handleTokenEvent);
    return () => {
      window.removeEventListener(TOKEN_EVENT, handleTokenEvent);
    };
  }, [handleTokenEvent]);

  // Cleanup when user disconnects (accountId becomes empty)
  useEffect(() => {
    if (!accountId) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(true);
      setWarningVisible(false);
      setLastCursor(null);
      setLastCursorId(null);
      failureCountRef.current = 0;
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [accountId]);

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const clearAllPendingUndos = useCallback(() => {
    pendingUndos.current.forEach((entry) => {
      clearTimeout(entry.timer);
    });
    pendingUndos.current.clear();
  }, []);

  const commitDismiss = useCallback(
    async (id: string) => {
      try {
        await dismissNotificationAPI(id);
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 404) return;
        chakraToast({
          status: 'error',
          description: 'Failed to dismiss notification',
          duration: 3000,
          position: 'top-right',
        });
        // Restore — API failed, put it back
        const pending = pendingUndos.current.get(id);
        if (pending) {
          setNotifications(prev => [pending.notification, ...prev]);
          if (pending.notification.status === 'UNREAD') {
            setUnreadCount(prev => prev + 1);
          }
          pendingUndos.current.delete(id);
        }
      }
    },
    [chakraToast],
  );

  const handleDismissNotification = useCallback(
    (id: string) => {
      const target = notificationsRef.current.find(n => n._id === id);
      if (!target) return;

      // Optimistic remove from UI
      setNotifications(prev => prev.filter(n => n._id !== id));
      setUnreadCount(prev => (target.status === 'UNREAD' ? Math.max(prev - 1, 0) : prev));

      if (target.type === 'SIGNATURE_NEEDED') {
        // Cancel any existing undo timer for this specific notification
        const existing = pendingUndos.current.get(id);
        if (existing) {
          clearTimeout(existing.timer);
        }

        // Delay API call — allow undo within 5s
        const timer = setTimeout(() => {
          pendingUndos.current.delete(id);
          commitDismiss(id);
        }, 5000);

        pendingUndos.current.set(id, { notification: target, timer });

        chakraToast({
          duration: 5000,
          position: 'top-right',
          render: ({ onClose }) => (
            <Box
              bg="#333"
              color="white"
              p={3}
              borderRadius={8}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Text fontSize="sm">Notification dismissed</Text>
              <Button
                size="xs"
                variant="outline"
                color="white"
                onClick={() => {
                  const pending = pendingUndos.current.get(id);
                  if (pending) {
                    clearTimeout(pending.timer);
                    setNotifications(prev => [pending.notification, ...prev]);
                    if (pending.notification.status === 'UNREAD') {
                      setUnreadCount(prev => prev + 1);
                    }
                    pendingUndos.current.delete(id);
                  }
                  onClose();
                }}
              >
                Undo
              </Button>
            </Box>
          ),
        });
      } else {
        // Non-SIGNATURE_NEEDED: dismiss immediately
        commitDismiss(id);
      }
    },
    [chakraToast, commitDismiss],
  );

  const handleDismissAll = useCallback(async () => {
    // Cancel any pending undo timers before bulk dismiss
    clearAllPendingUndos();

    try {
      await dismissAllAPI();
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      chakraToast({
        status: 'error',
        description: 'Failed to dismiss all notifications',
        duration: 3000,
        position: 'top-right',
      });
    }
  }, [chakraToast, clearAllPendingUndos]);

  const handleMarkSeen = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      await markSeenAPI(ids);
      setNotifications(prev => {
        let seenCount = 0;
        const updated = prev.map(n => {
          if (ids.includes(n._id) && n.status === 'UNREAD') {
            seenCount++;
            return { ...n, status: 'SEEN' as const };
          }
          return n;
        });
        if (seenCount > 0) {
          setUnreadCount(c => Math.max(c - seenCount, 0));
        }
        return updated;
      });
    } catch {
      // Silent — no user-facing error for mark-seen failures
    }
  }, []);

  const handleNavigateToNotification = useCallback((notification: Notification) => {
    if (notification.status === 'UNREAD') {
      handleMarkSeen([notification._id]);
    }

    let route: string;
    switch (notification.type) {
      case 'WALLET_CONFIG_UPDATED':
        route = `${ApplicationRoutes.Dashboard}/${notification.walletId}/settings/${notification.walletAccountId}`;
        break;
      case 'WALLET_INVITATION':
        route = '/';
        break;
      case 'SIGNATURE_NEEDED':
      case 'TRANSACTION_COMPLETED': {
        // Fund notifications route to the Activity tab (incoming transactions);
        // use transactionType field with message fallback for older notifications
        const isFund = notification.transactionType === 'Fund'
          || notification.message.toLowerCase().includes('fund deposit');
        const section = isFund ? 'activity' : 'transactions';
        const basePath = `${ApplicationRoutes.Dashboard}/${notification.walletId}/${section}/${notification.walletAccountId}`;
        route = notification.transactionId
          ? `${basePath}?expand=${notification.transactionId}`
          : basePath;
        break;
      }
      default:
        route = `${ApplicationRoutes.Dashboard}/${notification.walletId}/transactions/${notification.walletAccountId}`;
        break;
    }
    navigateRef.current(route);
    setIsPanelOpen(false);
  }, [handleMarkSeen]);

  const contextValue = useMemo<NotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      isPanelOpen,
      warningVisible,
      lastCursor,
      lastCursorId,
      togglePanel,
      dismissNotification: handleDismissNotification,
      dismissAll: handleDismissAll,
      markSeen: handleMarkSeen,
      navigateToNotification: handleNavigateToNotification,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      isPanelOpen,
      warningVisible,
      lastCursor,
      lastCursorId,
      togglePanel,
      handleDismissNotification,
      handleDismissAll,
      handleMarkSeen,
      handleNavigateToNotification,
    ],
  );

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
};
